import { Divider } from "components/design/Divider";
import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import { DeleteIcon } from "components/icons/delete";
import { FieldArray, FieldArrayRenderProps } from "formik";
import { FormikForm } from "components/FormikForm/FormikForm";
import { groupDetails } from "formValidations/salesCrm";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import React from "react";
import { useCheckValidEmailMobileMutation } from "store/apis/salesCrm";
import { checkEmptyObject } from "utils/checkEmptyObject";
import Modal from "components/DesignSystem/Modal/Modal";

type UserDataPayLoad = {
  first_name: string;
  last_name: string;
  email: string;
  linkedin: string;
};

type AddTeamMemberProps = {
  memberNumber: number;
  firstName: string;
  lastName: string;
  email: string;
  linkedin: string;
  remove: FieldArrayRenderProps["remove"];
};

type NewGroupCreationModalProps = {
  showModal: boolean | undefined;
  setShowModal: (newState: React.SetStateAction<boolean>) => void;
  setShowEntityModal: (newState: React.SetStateAction<boolean>) => void;
  userDataPayload: UserDataPayLoad | null;
  setUserDataPayLoad: (
    newState: React.SetStateAction<UserDataPayLoad[]>
  ) => void;
  setGroupName: (newState: React.SetStateAction<string>) => void;
  fromProfiles?: boolean;
};

type FormValues = {
  group_name: string;
  leadfounder_first_name: string;
  leadfounder_last_name: string;
  leadfounder_email: string;
  leadfounder_linkedin: string;
  users_data: UserDataPayLoad[];
};

const EMPTY_TEAM_MEMBER: UserDataPayLoad = {
  first_name: "",
  last_name: "",
  email: "",
  linkedin: "",
};

const AddTeamMember = ({
  memberNumber,
  firstName,
  lastName,
  email,
  linkedin,
  remove,
}: AddTeamMemberProps) => {
  const handleDeleteEntity = () => {
    remove(memberNumber);
  };

  return (
    <div className="t-flex t-flex-col t-w-full t-gap-2">
      <div className="t-flex t-justify-between t-items-center">
        <p className="t-text-text-60 t-text-body t-font-semibold t-m-0">
          Team Member
        </p>
        <Button
          customType="ghost_icon"
          type="button"
          size="xsmall"
          onClick={handleDeleteEntity}
        >
          <DeleteIcon />
        </Button>
      </div>
      <Divider />
      <div className="t-flex t-w-full t-gap-3">
        <Input
          withForm
          autoFocus
          name={firstName}
          label="First name"
          placeholder="Enter first name"
          required
        />
        <Input
          withForm
          name={lastName}
          label="Last name"
          placeholder="Enter last name"
          required
        />
      </div>

      <Input
        withForm
        name={email}
        type="email"
        label="Email"
        placeholder="Enter email"
        required
      />
      <Input
        withForm
        name={linkedin}
        label="LinkedIn"
        placeholder="LinkedIn link"
      />
    </div>
  );
};

const NewGroupCreationModal = ({
  showModal,
  setShowModal,
  setShowEntityModal,
  userDataPayload,
  setUserDataPayLoad,
  setGroupName,
  fromProfiles = false,
}: NewGroupCreationModalProps) => {
  const { alertToast } = useToast();
  const { isForeignCA, isUserTypeForeignCA } = useRoleBasedView();
  const isFCA = isUserTypeForeignCA || isForeignCA;

  const [checkValidEmailMobile] = useCheckValidEmailMobileMutation();

  const initialValues: FormValues = {
    group_name: "",
    leadfounder_first_name: userDataPayload?.first_name || "",
    leadfounder_last_name: userDataPayload?.last_name || "",
    leadfounder_email: userDataPayload?.email || "",
    leadfounder_linkedin: userDataPayload?.linkedin || "",
    users_data: [],
  };

  const checkEmailMobile = async (values: FormValues) => {
    const emails = [
      values.leadfounder_email,
      ...values.users_data.map(({ email }) => email),
    ];
    const emailMobileData = { emails };

    try {
      let existingEmails = false;
      if (!fromProfiles) {
        const response = await checkValidEmailMobile(emailMobileData).unwrap();
        existingEmails = response?.emails_already_exits.length > 0;
      }

      if (existingEmails) {
        alertToast({ message: "Existing Emails found" });
        setShowModal(false);
        return;
      }

      setUserDataPayLoad([
        {
          first_name: values.leadfounder_first_name,
          last_name: values.leadfounder_last_name,
          email: values.leadfounder_email,
          linkedin: values.leadfounder_linkedin,
        },
        ...values.users_data,
      ]);
      setGroupName(values.group_name);
      setShowEntityModal(true);
    } catch (e: any) {
      setShowModal(false);
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  return (
    <Modal.Root open={showModal} onOpenChange={handleClose}>
      <Modal.Content asChild>
        <FormikForm<FormValues>
          initialValues={initialValues}
          validationSchema={groupDetails}
          onSubmit={checkEmailMobile}
          validateOnChange={false}
        >
          {({ values, isSubmitting }) => {
            let isAddMemberBtnDisabled = false;
            if (values.users_data.length > 0) {
              const index = values.users_data.length - 1;
              const lastMember = {
                first_name: values.users_data[index]?.first_name,
                last_name: values.users_data[index]?.last_name,
                email: values.users_data[index]?.email,
              };
              isAddMemberBtnDisabled = !checkEmptyObject(lastMember);
            }

            return (
              <>
                <Modal.Header>
                  <Modal.Title>
                    {isFCA ? "New Client" : "New Group"}
                  </Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body className="t-flex t-max-h-[500px] t-flex-col t-gap-3">
                  <Input
                    withForm
                    required
                    name="group_name"
                    label="Brand name"
                    placeholder="Enter the name"
                  />
                  <div className="t-flex t-flex-col t-gap-3 t-rounded t-bg-surface-lighter-grey t-p-4">
                    <p className="t-text-text-60 t-text-body t-font-semibold t-m-0">
                      Lead Founder
                    </p>
                    <Divider />
                    <div className="t-flex t-w-full t-gap-3">
                      <Input
                        withForm
                        required
                        name="leadfounder_first_name"
                        label="First name"
                        placeholder="Enter first name"
                      />
                      <Input
                        withForm
                        required
                        name="leadfounder_last_name"
                        label="Last name"
                        placeholder="Enter last name"
                      />
                    </div>

                    <Input
                      withForm
                      required
                      name="leadfounder_email"
                      infoText={
                        isFCA &&
                        "For record only, Inkle will not contact the founder"
                      }
                      label="Email"
                      placeholder="Enter email"
                      disabled={fromProfiles}
                    />
                    {!isFCA && (
                      <Input
                        withForm
                        name="leadfounder_linkedin"
                        label="Linkedin"
                        placeholder="LinkedIn"
                      />
                    )}
                  </div>

                  <FieldArray name="users_data">
                    {({ push, remove }) => (
                      <>
                        {values.users_data.map((_, index) => (
                          <div
                            className="t-rounded t-bg-surface-lighter-grey t-p-4"
                            key={`team-member-${index}`}
                          >
                            <AddTeamMember
                              memberNumber={index}
                              firstName={`users_data.${index}.first_name`}
                              lastName={`users_data.${index}.last_name`}
                              email={`users_data.${index}.email`}
                              linkedin={`users_data.${index}.linkedin`}
                              remove={remove}
                            />
                          </div>
                        ))}
                        <div className="t-mr-auto">
                          <Button
                            size="small"
                            customType="secondary"
                            disabled={isAddMemberBtnDisabled}
                            type="button"
                            onClick={() => push(EMPTY_TEAM_MEMBER)}
                          >
                            + Another Team Member
                          </Button>
                        </div>
                      </>
                    )}
                  </FieldArray>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Button
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    customType="primary"
                    size="regular"
                    type="submit"
                  >
                    Next
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};

export default NewGroupCreationModal;
